<template>
  <div>
    <div class="container-fluid">
      <Breadcrumbs main="Реальные пользователи" title="Список реальных пользователей"/>
      <b-card>
        <div class="d-flex align-items-center mb-4">
          <SearchInput v-model="searchValue" @search="applySearch" :placeholder-name="searchPlaceholder"/>
          <b-form-select
              v-model="searchField"
              :options="searchFieldOptions"
              size="sm"
              class="ml-2"
              style="width: 220px"
          ></b-form-select>
        </div>
        <div v-if="isLoadingUsers" class="loading-wrapper">
          <b-spinner class="spinner"></b-spinner>
        </div>
        <b-table
            v-else
            responsive
            show-empty
            stacked="md"
            :striped="striped"
            :bordered="bordered"
            :fields="fields"
            :items="users"
            :tbody-tr-class="trClass"
            :current-page=0
            :per-page=10
            :sort-by.sync="sortByField"
            :sort-desc.sync="sortDesc"
            no-local-sorting
            @sort-changed="onSortChanged"
            class="mb-5"
        >
          <template #cell(username)="data">
            <div>{{ data.item.username }}</div>
          </template>
          <template #cell(email)="data">
            <div>{{ data.item.email }}</div>
          </template>
          <template #cell(status)="data">
            <div v-if="data.item.game_profiles_count > 0">Игровой /</div>
            <div v-else>Не игровой /</div>
            <div v-if="data.item?.verified_social_accounts_count > 0">Активирован</div>
            <div v-else>Не активирован</div>
          </template>
          <template #cell(action)="data">
            <div class="row justify-content-center">
              <b-btn variant="primary" class="mr-1 p-1" @click="showUserInfo(data.item.id)"><i
                  class="fa fa-eye"></i></b-btn>
              <div v-if="data.item.currentBans.length">
                <div v-for="bans in data.item.currentBans" :key="bans.id">
                  <b-btn variant="warning p-1" @click="deleteUserBan({userId: bans.id})">
                    <i class="fa fa-ban"></i>
                  </b-btn>
                </div>
              </div>
              <div v-else>
                <b-btn variant="danger p-1" @click="showUserBanModal(data.item.id)">
                  <i class="fa fa-ban"></i>
                </b-btn>
              </div>
            </div>
          </template>
        </b-table>
        <b-col md="6" class="p-0">
          <b-pagination
              @change="handlePageChange"
              v-model="currentPage"
              :total-rows="totalUserPagination"
              :per-page="10"
              class="my-0"
          ></b-pagination>
        </b-col>
      </b-card>
    </div>
    <Modal name="BanUser" title="Бан пользователя" v-model="isShowBanUserModal" :params="{ id: selectedUserId }" @submit="onBanSuccess"/>
  </div>
</template>

<script>
import {UsersAdapterMixin} from "../../mixins/Users/UsersAdapterMixin";
import {ModalAdapterMixin} from "@/mixins/modal/ModalAdapterMixin";
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";
import {UserBanAdapterMixin} from "@/mixins/UserBan/UserBanAdapterMixin";
import SearchInput from "@/components/SearchInput.vue";
import Modal from '@/components/modals/index.vue'

const SORT_FIELD_MAP = {
  id: 'users.id',
  username: 'username',
  email: 'email',
  status: 'role',
}

const SEARCH_FIELD_OPTIONS = [
  {value: 'id', text: 'По ID'},
  {value: 'username', text: 'По username'},
  {value: 'email', text: 'По email'},
  {value: 'teams.name', text: 'По команде'},
  {value: 'game_profile_game_id', text: 'По Game ID (PUBG/MLBB)'},
  {value: 'game_profile_nick', text: 'По игровому нику'},
  {value: 'social_account_username', text: 'По соц. аккаунту'},
]

const SEARCH_PLACEHOLDERS = {
  id: 'Введите ID пользователя',
  username: 'Введите username',
  email: 'Введите email',
  'teams.name': 'Введите название команды',
  game_profile_game_id: 'Введите PUBG ID или MLBB ID',
  game_profile_nick: 'Введите игровой ник',
  social_account_username: 'Введите username соц. аккаунта',
}

export default {
  name: "ListUsers",
  components: {SearchInput, Modal},
  mixins: [UsersAdapterMixin, ModalAdapterMixin, PaginationHelperMixin, UserBanAdapterMixin],
  created() {
    this.isLoadingUsers = true
    this.recordPageToStorage(this.currentPage, this.pageKey)
    this.grabUsers({
      pageNumber: this.getCurrentPageNumber(this.pageKey),
    }).finally(() => {
      this.isLoadingUsers = false
    })
  },

  data() {
    return {
      searchValue: '',
      searchField: 'username',
      searchFieldOptions: SEARCH_FIELD_OPTIONS,
      pageKey: 'pageNumberUsersList',
      sortByField: 'id',
      sortDesc: true,
      fields: [
        {key: 'id', label: '№', sortable: true},
        {key: 'username', label: 'Имя', sortable: true},
        {key: 'email', label: 'Почта', sortable: true},
        {key: 'status', label: 'Статус', sortable: true},
        {key: 'action', label: 'Инструменты', sortable: false},
      ],
      striped: true,
      bordered: true,
      isLoadingUsers: false,
      isShowBanUserModal: false,
      selectedUserId: null,
    }
  },

  computed: {
    searchPlaceholder() {
      return SEARCH_PLACEHOLDERS[this.searchField] || 'Поиск пользователей'
    },
    currentSort() {
      const apiField = SORT_FIELD_MAP[this.sortByField] || this.sortByField
      return this.sortDesc ? `-${apiField}` : apiField
    },
  },

  methods: {
    buildRequestParams(pageNumber) {
      const params = {
        pageNumber,
        filters: {},
        sort: this.currentSort,
      }
      if (this.searchValue) {
        params.filters[this.searchField] = this.searchValue
      }
      return params
    },

    handlePageChange(pageNumber) {
      this.recordPageToStorage(pageNumber, this.pageKey)
      this.isLoadingUsers = true
      this.grabUsers(this.buildRequestParams(pageNumber)).finally(() => {
        this.isLoadingUsers = false
      })
    },

    applySearch() {
      this.isLoadingUsers = true
      this.grabUsers(this.buildRequestParams(1)).finally(() => {
        this.isLoadingUsers = false
      })
    },

    onSortChanged(ctx) {
      this.sortByField = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.applySearch()
    },

    trClass: rowData => rowData?.currentBans.length && 'table-dark',

    showUserBanModal(userId) {
      this.selectedUserId = userId
      this.isShowBanUserModal = true
    },

    onBanSuccess() {
      this.isLoadingUsers = true
      this.grabUsers(this.buildRequestParams(this.getCurrentPageNumber(this.pageKey))).finally(() => {
        this.isLoadingUsers = false
      })
    },
  },
}
</script>
